import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { enviosApi } from '../api/envios';
import { Envio } from '../types/envio';
import { normalizePaginatedResponse, PaginatedResponse } from '../lib/pagination';
import { toast } from 'sonner';

interface UseEnviosParams {
  search?: string;
  estado?: string;
  incluirOcultos?: string;
  page?: number;
  limit?: number;
}

/**
 * Dos modos:
 * - Con params de paginación (page/limit) → devuelve PaginatedResponse<Envio>.
 * - Sin params de paginación → devuelve Envio[] (compatibilidad con vistas que
 *   necesitan todos los datos, ej. MisEnviosRepartidor).
 */
export function useEnvios(): {
  data: Envio[] | undefined;
  isLoading: boolean;
  isError: boolean;
  refetch: () => void;
};
export function useEnvios(params: UseEnviosParams & { page: number; limit: number }): {
  data: PaginatedResponse<Envio> | undefined;
  isLoading: boolean;
  isError: boolean;
  refetch: () => void;
};
export function useEnvios(params?: UseEnviosParams) {
  const hasPagination = !!params?.page && !!params?.limit;

  return useQuery({
    queryKey: ['envios', params],
    queryFn: async () => {
      const response = await enviosApi.getAll(params);

      if (hasPagination) {
        return normalizePaginatedResponse<Envio>(response);
      }

      // Modo sin paginación: devolvemos array plano para compatibilidad.
      return Array.isArray(response?.data) ? response.data : [];
    },
    staleTime: 1000 * 30,
    refetchInterval: 1000 * 10,
    refetchIntervalInBackground: true,
    placeholderData: keepPreviousData,
  });
}

export function useEnvio(id: number) {
  return useQuery({
    queryKey: ['envios', id],
    queryFn: () => enviosApi.getById(id),
    enabled: !!id && id > 0,
    staleTime: 1000 * 60 * 2,
  });
}

export function useEnvioStats() {
  return useQuery({
    queryKey: ['envios', 'stats'],
    queryFn: () => enviosApi.getStats(),
    staleTime: 1000 * 60 * 5,
  });
}

export function useCreateEnvio() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: any) => enviosApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['envios'] });
      toast.success('Envío creado exitosamente');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Error al crear el envío');
    },
  });
}

export function useUpdateEnvio() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: any }) =>
      enviosApi.update(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['envios'] });
      queryClient.invalidateQueries({ queryKey: ['envios', id] });
      toast.success('Envío actualizado exitosamente');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Error al actualizar el envío');
    },
  });
}

export function useOcultarEnvio() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => enviosApi.ocultar(id),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['envios'] });
      queryClient.invalidateQueries({ queryKey: ['envios', data.id] });
      toast.success(data.oculto ? 'Envío ocultado' : 'Envío visible');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Error al ocultar el envío');
    },
  });
}

export function useDeleteEnvio() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => enviosApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['envios'] });
      toast.success('Envío eliminado exitosamente');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Error al eliminar el envío');
    },
  });
}